import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useContext,
  useState,
} from "react";
import {
  AdyenCheckout,
  Dropin,
  Card,
  ApplePay,
  GooglePay,
} from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import "../../adyen-overrides.css";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { initCardPayment, finalizeCardPayment } from "@api";
import {
  isApplePayCancellation,
  normalizeAmount,
  getTranslations,
  navigateOnPaymentCompletion,
  buildDropinConfiguration,
  onSubmitFactory,
  resolveInitData,
  scrollFocusedFieldIntoView,
  revealPaymentError,
} from "../shared";

const AdyenPaymentAdvancedFlow = ({
  paymentParameters, // { amount, currency, reference, returnUrl, … }
  processingUrl,
  onError,
  externalSubmitRef,
  onAmountChange,
  setLoading,
  onCardTypeChange,
  onAmexChange,
  onCardBrandChange,
  onCardNotAllowed,
  onStorePaymentDetails,
  disablePayment,
  setProcessing,
  setBinLookupPending,
}) => {
  const navigate = useNavigate();
  const dropinRef = useRef(null);
  const paymentDataRef = useRef(null);
  const idempotencyKeyRef = useRef(uuidv4());
  const disablePaymentRef = useRef(disablePayment);
  const [errorMessage, setErrorMessage] = useState(null);

  console.log("Advanced Flow");

  useEffect(() => {
    disablePaymentRef.current = disablePayment;
  }, [disablePayment]);

  // Secured card fields report focus via the card onFocus config; the billing-address
  // inputs are plain inputs, so catch their focus here to scroll them up too (mobile).
  useEffect(() => {
    const container = document.getElementById("adyen-dropin-container");
    if (!container) return;
    const onFocusIn = () => scrollFocusedFieldIntoView();
    container.addEventListener("focusin", onFocusIn);
    return () => container.removeEventListener("focusin", onFocusIn);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function init() {
      if (!mounted) return;

      // Show skeleton while (re)initializing
      setLoading?.(true);

      if (dropinRef.current) {
        dropinRef.current.unmount();
        dropinRef.current = null;
      }

      const initPaymentResponse = resolveInitData(
        await initCardPayment({
          merchantId: paymentParameters.merchantId,
          amount: paymentParameters.amount,
          cardAccountType: "Any",
          paymentProvider: "Adyen",
        }),
        "payment"
      );

      onAmountChange?.(initPaymentResponse.amount);

      const { paymentMethods, clientKey, environment } = JSON.parse(
        initPaymentResponse.clientData
      );
      const adyenPaymentMethodsData = JSON.parse(paymentMethods);
      const applePayConfig = adyenPaymentMethodsData.paymentMethods?.find(
        (method) => method.type === "applepay",
      )?.configuration;

      let globalConfiguration = {
        locale: "en-US",
        countryCode: "GB",
        environment: environment.toLowerCase(),
        showPayButton: false,
        clientKey: clientKey,
        paymentMethodsResponse: adyenPaymentMethodsData,
        amount: { 
          value: (paymentParameters?.amount || 0) * 100,
          currency: 'GBP' 
        },
        onSubmit: onSubmitFactory({
          disablePaymentRef,
          paymentParameters,
          onCardNotAllowed,
          onError,
          setProcessing,
          authoritativeAmount: normalizeAmount(initPaymentResponse.amount),
          onStorePaymentDetails,
          paymentDataRefRef: paymentDataRef,
          idempotencyKeyRef,
          instantPaymentTypes: ["applepay", "googlepay"],
        }),

        onAdditionalDetails: async (state, component, actions) => {
          try {
            const finalizeCardPaymentResponse = await finalizeCardPayment({
              merchantId: paymentParameters.merchantId,
              clientData: JSON.stringify(state.data),
              paymentProvider: "Adyen",
              paymentId: paymentDataRef.current?.id,
            });

            const result = JSON.parse(finalizeCardPaymentResponse.clientData);

            // If the /payments/details request from your server fails, or if an unexpected error occurs.
            if (!result.resultCode) {
              actions.reject();
              return;
            }

            const { resultCode, action, order, donationToken } = result;

            actions.resolve({
              resultCode,
              action,
              order,
              donationToken,
            });
          } catch (error) {
            console.error("onSubmit", error);
            actions.reject();
          }
        },
        onPaymentCompleted: (result, component) => {
          console.log("Payment completed:", result);

          // https://docs.adyen.com/online-payments/build-your-integration/payment-result-codes/
          if (
            result.resultCode === "Authorised" ||
            result.resultCode === "Pending" ||
            result.resultCode === "Received"
          ) {
            navigateOnPaymentCompletion({
              navigate,
              baseUrl: processingUrl,
              paymentId: paymentDataRef.current?.id,
              fallbackPath: "/",
            });
          } else {
            // This path should never be reached, because declined payments trigger onPaymentFailed rather than onPaymentCompleted
            setErrorMessage("Your payment was declined. Please try again.");
            onError();
            revealPaymentError();
          }
        },
        onPaymentFailed: (result, component) => {
          console.warn("Payment failed", result);
          setErrorMessage(
            "Payment failed. Please check your details and try again or use a different payment method."
          );
          onError?.();
          dropinRef.current.setStatus("ready");
          revealPaymentError();
          idempotencyKeyRef.current = uuidv4();
        },
        onError: (error, component) => {
          if (isApplePayCancellation(error)) {
            console.log("Apple Pay cancelled by user - not showing error");
            setErrorMessage(null);
            init();
          } else if (error?.cause?.statusCode === "CANCELED") {
            // Keep the original check for other cancellation types
            setErrorMessage(null);
            init();
          } else {
            console.error(error);
            setErrorMessage(
              "An unknown error occurred. Please try again later."
            );
            revealPaymentError();
            onError?.(error);
          }
        },

        translations: getTranslations(),
      };

      const checkout = await AdyenCheckout(globalConfiguration);

      const dropinConfiguration = buildDropinConfiguration({
        paymentMethodComponents: [Card, GooglePay, ApplePay],
        paymentParameters,
        setLoading,
        onAmexChange,
        onCardBrandChange,
        onCardTypeChange,
        setBinLookupPending,
        instantPaymentTypes: ["applepay", "googlepay"],
        applePayContext: {
          isInIframe: window.self !== window.top,
          clientKey,
          environment,
          applePayConfig,
        },
      });

      const dropin = new Dropin(checkout, dropinConfiguration).mount(
        "#adyen-dropin-container"
      );

      dropinRef.current = dropin;
    }

    init();

    return () => {
      mounted = false;
      // only need to unmount the Drop-in itself
      dropinRef.current?.unmount();
    };
  }, []);

  useImperativeHandle(externalSubmitRef, () => ({
    submitPayment: () => {
      if (dropinRef.current) {
        console.log("Submitting Adyen payment via Drop-in");
        setErrorMessage(null);
        dropinRef.current.submit();
      } else {
        console.warn(
          "Drop-in is not mounted yet or payment is currently in progress"
        );
      }
    },
  }));

  return (
    <>
      {errorMessage && (
        <div className="text-red-600 text-sm rounded-3 p-2 mb-4" role="alert">
          {errorMessage}
        </div>
      )}
      <div id="adyen-dropin-container" />
    </>
  );
};

export { AdyenPaymentAdvancedFlow };
